import React, { useState } from 'react';
import {
  ShoppingCart, UserPlus, Undo2, Wrench, CheckCircle2,
  Ban, RotateCcw, Pencil, Trash2, History, Loader2,
  StickyNote, ArrowRightLeft, RefreshCw, CalendarClock, MapPin,
} from 'lucide-react';
import { cn, Button, Dialog } from '../../ui';
import NotaDeHistorial from './NotaDeHistorial';
import { textoSitio } from '../utils/ubicacion';

/**
 * Timeline
 *
 * Historial cronológico de eventos de un activo (más reciente arriba).
 * Colores de pill coherentes con el Dashboard (III.2).
 *
 * El backend devuelve eventos con campos:
 *   - fecha           (DATEONLY del evento)
 *   - created_at      (DATETIME de registro)
 *   - user_nombre     (persona VINCULADA al evento — p.ej. el asignado)
 *   - creator_nombre  (persona que REGISTRÓ el evento — el actor)
 *   - motivo, notas, datos…
 */

const META = {
  compra: { Icon: ShoppingCart, pill: 'bg-violet-100 text-violet-800', dot: 'bg-violet-400', label: 'Compra' },
  asignacion: { Icon: UserPlus, pill: 'bg-blue-100 text-blue-800', dot: 'bg-blue-400', label: 'Asignación' },
  devolucion: { Icon: Undo2, pill: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-400', label: 'Devolución' },
  reparacion_inicio: { Icon: Wrench, pill: 'bg-amber-100 text-amber-800', dot: 'bg-amber-400', label: 'Entra en reparación' },
  reparacion_fin: { Icon: CheckCircle2, pill: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-400', label: 'Reparación finalizada' },
  baja: { Icon: Ban, pill: 'bg-rose-100 text-rose-800', dot: 'bg-rose-400', label: 'Baja' },
  reactivacion: { Icon: RotateCcw, pill: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-400', label: 'Reactivación' },
  traspaso: { Icon: ArrowRightLeft, pill: 'bg-sky-100 text-sky-800', dot: 'bg-sky-400', label: 'Traspaso' },
  cambio_estado: { Icon: RefreshCw, pill: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400', label: 'Cambio de estado' },
  nota: { Icon: StickyNote, pill: 'bg-amber-100 text-amber-800', dot: 'bg-amber-400', label: 'Nota' },
  movimiento: { Icon: MapPin, pill: 'bg-teal-100 text-teal-800', dot: 'bg-teal-400', label: 'Movimiento' },
  edicion: { Icon: Pencil, pill: 'bg-slate-100 text-slate-600', dot: 'bg-slate-300', label: 'Edición' },
  soft_delete: { Icon: Trash2, pill: 'bg-rose-100 text-rose-800', dot: 'bg-rose-400', label: 'Eliminado' },
  restore: { Icon: RotateCcw, pill: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-400', label: 'Restaurado' },
};

// Formato DATEONLY: "18 mar 2026". El backend manda `fecha` como
// "YYYY-MM-DD"; JS la parsea bien con new Date(). Sin hora — la
// hora exacta del registro la mostramos como tooltip en el título.
function fmtFechaSolo(s) {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return d.toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

// Tooltip más detallado: el created_at completo si lo hay, para que
// haciendo hover sepas la hora exacta del registro.
function fmtTooltip(createdAt) {
  if (!createdAt) return undefined;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return undefined;
  return `Registrado: ${d.toLocaleString('es-ES', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })}`;
}

// «editada · 19 oct, 16:05»: cuándo se cambió por última vez una nota (Fase 2b).
function fmtEditada(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function Timeline({ eventos, onEditarFecha, onEliminar, onEditarNota, onBorrarNota }) {
  // Estado local para los dos modales (editar fecha y eliminar). Se
  // guardan los datos del evento sobre el que se está actuando para
  // pintar título/descripción y construir el payload del PUT/DELETE.
  const [editing, setEditing] = useState(null); // {evento, fecha, reason}
  const [deleting, setDeleting] = useState(null); // {evento, reason}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Permitimos las acciones de fila solo si el padre nos pasó los
  // callbacks. Útil para ocultarlas en listados read-only.
  const canManage = typeof onEditarFecha === 'function' && typeof onEliminar === 'function';

  async function confirmEdit() {
    setErr(null);
    if (!editing?.fecha) {
      setErr('La fecha es obligatoria');
      return;
    }
    setBusy(true);
    try {
      await onEditarFecha(editing.evento.id, editing.fecha, editing.reason);
      setEditing(null);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'No se pudo guardar');
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    setErr(null);
    if (!deleting?.reason?.trim()) {
      setErr('El motivo de la eliminación es obligatorio');
      return;
    }
    setBusy(true);
    try {
      await onEliminar(deleting.evento.id, deleting.reason.trim());
      setDeleting(null);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'No se pudo eliminar');
    } finally {
      setBusy(false);
    }
  }

  if (!eventos || eventos.length === 0) {
    return (
      <div className="text-xs text-slate-400 flex items-center gap-2 py-4">
        <History className="w-4 h-4" />
        Sin eventos registrados todavía.
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Línea vertical */}
      <div className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200" aria-hidden="true" />
      <ul className="space-y-3">
        {eventos.map((ev) => {
          const m = META[ev.event_type] || {
            Icon: History, pill: 'bg-slate-100 text-slate-600', dot: 'bg-slate-300', label: ev.event_type,
          };
          const Icon = m.Icon;
          return (
            <li key={ev.id} className="relative pl-6">
              <span
                className={cn(
                  'absolute left-0 top-1 w-[15px] h-[15px] rounded-full border-2 border-white',
                  m.dot
                )}
              />
              <div className="flex items-center gap-2 flex-wrap">
                <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium', m.pill)}>
                  <Icon className="w-3 h-3" />
                  {m.label}
                </span>
                {/* En una Asignación mostramos a QUIÉN se asignó. En el
                    resto de eventos basta con el actor (creator) — el
                    user_id puede no tener significado o ser el mismo. */}
                {ev.event_type === 'asignacion' && ev.user_nombre && (
                  <span className="text-xs text-slate-700">
                    a <span className="font-medium">{ev.user_nombre}</span>
                  </span>
                )}
                {/* Actor: quién registró el evento (clic en el botón). */}
                {ev.creator_nombre && (
                  <span className="text-xs text-slate-500">
                    por <span className="font-medium text-slate-700">{ev.creator_nombre}</span>
                  </span>
                )}
                <span
                  className="text-[11px] text-slate-400 ml-auto"
                  title={fmtTooltip(ev.created_at)}
                >
                  {fmtFechaSolo(ev.fecha)}
                </span>
                {/* Una nota cambiada lo dice. El tooltip enseña quién y por qué, NO el texto
                    anterior: las versiones quedan para auditoría, no se muestran (Fase 2b). */}
                {ev.event_type === 'nota' && ev.edited_at && (
                  <span
                    className="text-[11px] text-slate-400"
                    title={`Editada${ev.editor_nombre ? ` por ${ev.editor_nombre}` : ''}${ev.edit_reason ? ` · ${ev.edit_reason}` : ''}`}
                  >
                    · editada {fmtEditada(ev.edited_at)}
                  </span>
                )}
                {canManage && (
                  <span className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => {
                        setErr(null);
                        setEditing({ evento: ev, fecha: ev.fecha || '', reason: '' });
                      }}
                      title="Editar fecha"
                      aria-label="Editar fecha del evento"
                      className="h-6 w-6 flex items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                      <CalendarClock className="w-3.5 h-3.5" />
                    </button>
                    {/* Una nota se borra desde la propia nota (con su regla y su motivo); aquí
                        habría dos papeleras para lo mismo. */}
                    {ev.event_type !== 'nota' && (
                    <button
                      type="button"
                      onClick={() => {
                        setErr(null);
                        setDeleting({ evento: ev, reason: '' });
                      }}
                      title="Eliminar evento"
                      aria-label="Eliminar evento del historial"
                      className="h-6 w-6 flex items-center justify-center rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    )}
                  </span>
                )}
              </div>
              {/* Movimiento (Fase 3): de dónde a dónde. El origen lo calcula el servidor (el
                  movimiento vivo anterior por fecha), porque el historial va paginado. */}
              {ev.event_type === 'movimiento' && ev.ubicacion_provincia && (
                <p className="text-sm text-slate-800 mt-1">
                  {textoSitio(ev.ubicacion_origen_provincia, ev.ubicacion_origen_localidad)
                    ? `${textoSitio(ev.ubicacion_origen_provincia, ev.ubicacion_origen_localidad)} → ${textoSitio(ev.ubicacion_provincia, ev.ubicacion_localidad)}`
                    : `Ubicación: ${textoSitio(ev.ubicacion_provincia, ev.ubicacion_localidad)}`}
                </p>
              )}
              {ev.motivo && (
                <p className="text-xs text-slate-600 mt-1">{ev.motivo}</p>
              )}
              {/* `notas` según su PAPEL (19/10, Fase 2), no según quién la escribió, que no se
                  puede saber: un evento `nota` es la nota de una persona (sólo la crea alguien en
                  pantalla); el texto en `notas` de otro evento es el «Detalle» de ese movimiento,
                  y en una compra, la nota que se escribió en Compras.
                  SIEMPRE TEXTO PLANO: React escapa el contenido, y `whitespace-pre-wrap` respeta
                  los saltos de línea sin interpretar nada. Nunca dangerouslySetInnerHTML. */}
              {ev.notas && ev.event_type === 'nota' && (
                // La clave cambia al editarse: el componente vuelve a nacer con el texto nuevo.
                <NotaDeHistorial
                  key={`${ev.id}-${ev.edited_at || ''}`}
                  ev={ev}
                  onEditar={onEditarNota}
                  onBorrar={onBorrarNota}
                />
              )}
              {ev.notas && ev.event_type !== 'nota' && (
                <p className="mt-1 whitespace-pre-wrap break-words text-xs text-slate-500">
                  <span className="font-medium text-slate-400">
                    {ev.event_type === 'compra' ? 'Nota de la compra'
                      : ev.event_type === 'movimiento' ? 'Nota del movimiento' : 'Detalle'}:
                  </span>{' '}
                  {ev.notas}
                </p>
              )}
              {ev.datos && typeof ev.datos === 'object' && (
                <p className="text-[11px] text-slate-400 mt-0.5 font-mono">
                  {Object.entries(ev.datos)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(' · ')}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {/* Modal: Editar fecha del evento */}
      <Dialog
        open={!!editing}
        onClose={() => !busy && setEditing(null)}
        title="Editar fecha del evento"
        description={
          editing
            ? `${META[editing.evento.event_type]?.label || editing.evento.event_type} · registrado el ${fmtFechaSolo(editing.evento.fecha)}`
            : ''
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => !busy && setEditing(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={confirmEdit} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Guardar
            </Button>
          </>
        }
      >
        {editing && (
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                Nueva fecha
              </label>
              <input
                type="date"
                value={editing.fecha}
                onChange={(e) => setEditing((s) => ({ ...s, fecha: e.target.value }))}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                Motivo de la edición (opcional)
              </label>
              <input
                value={editing.reason}
                onChange={(e) => setEditing((s) => ({ ...s, reason: e.target.value }))}
                placeholder="Ej: corrección de fecha real"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <p className="text-[11px] text-slate-400">
              Solo se modifica la fila histórica. El estado actual del activo
              (asignado, en reparación, etc.) no cambia.
            </p>
            {err && (
              <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2">
                {err}
              </p>
            )}
          </div>
        )}
      </Dialog>

      {/* Modal: Eliminar evento del historial */}
      <Dialog
        open={!!deleting}
        onClose={() => !busy && setDeleting(null)}
        title="Eliminar evento del historial"
        description={
          deleting
            ? `${META[deleting.evento.event_type]?.label || deleting.evento.event_type} del ${fmtFechaSolo(deleting.evento.fecha)}`
            : ''
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => !busy && setDeleting(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmDelete} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Eliminar
            </Button>
          </>
        }
      >
        {deleting && (
          <div className="space-y-3 text-sm">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
              Esto quita el evento del historial pero <span className="font-semibold">no deshace
              el estado actual del activo</span>. Si era una asignación todavía activa, el activo
              sigue mostrándose asignado a esa persona hasta que se registre un evento que
              lo cambie.
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                Motivo de la eliminación <span className="text-rose-500 normal-case">*</span>
              </label>
              <textarea
                value={deleting.reason}
                onChange={(e) => setDeleting((s) => ({ ...s, reason: e.target.value }))}
                rows={2}
                placeholder="Ej: evento duplicado / registrado por error"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            {err && (
              <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2">
                {err}
              </p>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}
